// Ax Article — Table des matières (refonte 2026).
// Construit la liste à partir des <h2> du corps rendu par la section ax-article
// (.ax-article__content). Ajoute un id (slug) aux h2 qui n'en ont pas, puis
// génère les ancres. Surligne le lien de la section active au scroll (scrollspy).
// Si aucun h2 → la TOC se masque. Cf. .claude/article-refonte-contract.md.

class AxArticleToc extends HTMLElement {
  /** @type {Array<{ heading: HTMLElement, link: HTMLAnchorElement }>} */
  #entries = [];
  /** @type {HTMLAnchorElement | null} */
  #activeLink = null;
  #ticking = false;
  /** @type {(() => void) | null} */
  #onScroll = null;

  connectedCallback() {
    const content = document.querySelector('.ax-article__content');
    const list = this.querySelector('.ax-article-toc__list');
    if (!content || !list) {
      this.hidden = true;
      return;
    }

    // Les carrousels produits insérés dans le corps (ax-article-carousel) ne portent pas de h2,
    // mais un contenu injecté plus tard dans leurs cartes ne doit pas entrer dans la liste.
    const headings = [...content.querySelectorAll('h2')].filter((heading) => !heading.closest('.ax-article-carousel'));
    if (headings.length === 0) {
      this.hidden = true;
      return;
    }

    // Les h2 sans id en reçoivent un (cible des ancres) — y compris quand la liste
    // vient du serveur, dont les href reprennent ce même slug.
    const usedIds = new Set();

    for (const [index, heading] of [...headings].entries()) {
      if (!heading.id) {
        heading.id = this.#uniqueSlug(heading.textContent, index, usedIds);
      }
      usedIds.add(heading.id);
    }

    // Liste déjà rendue côté serveur (cas normal, évite le CLS) : on ne reconstruit
    // pas, on se branche juste dessus pour le scrollspy.
    if (list.children.length > 0) {
      for (const link of list.querySelectorAll('.ax-article-toc__link')) {
        const heading = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (heading) this.#entries.push({ heading, link });
      }

      if (this.#entries.length > 0) {
        this.#setupScrollSpy();
        return;
      }
    }

    const fragment = document.createDocumentFragment();

    for (const heading of headings) {
      const item = document.createElement('li');
      item.className = 'ax-article-toc__item';

      const link = document.createElement('a');
      link.className = 'ax-article-toc__link underline-anim underline-anim--reverse';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();

      item.appendChild(link);
      fragment.appendChild(item);
      this.#entries.push({ heading, link });
    }

    list.appendChild(fragment);
    this.#setupScrollSpy();
  }

  disconnectedCallback() {
    if (!this.#onScroll) return;
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  #setupScrollSpy() {
    this.#onScroll = () => {
      if (this.#ticking) return;
      this.#ticking = true;
      requestAnimationFrame(() => {
        this.#updateActive();
        this.#ticking = false;
      });
    };

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#updateActive();
  }

  // Section active = dernier titre dont le haut a franchi une ligne à ~30% du
  // viewport (tolérant au header sticky, sans dépendre de sa hauteur).
  #updateActive() {
    const line = window.innerHeight * 0.3;
    let activeIndex = 0;

    for (const [index, entry] of this.#entries.entries()) {
      if (entry.heading.getBoundingClientRect().top - line <= 0) {
        activeIndex = index;
      } else {
        break;
      }
    }

    const activeLink = this.#entries[activeIndex]?.link;
    if (!activeLink || activeLink === this.#activeLink) return;

    this.#activeLink?.classList.remove('ax-article-toc__link--active');
    this.#activeLink?.removeAttribute('aria-current');
    activeLink.classList.add('ax-article-toc__link--active');
    activeLink.setAttribute('aria-current', 'true');
    this.#activeLink = activeLink;
  }

  #uniqueSlug(text, index, usedIds) {
    const base = (text || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const root = base ? `toc-${base}` : `toc-section-${index}`;
    let slug = root;
    let suffix = 2;
    while (usedIds.has(slug)) {
      slug = `${root}-${suffix}`;
      suffix += 1;
    }
    return slug;
  }
}

if (!customElements.get('ax-article-toc')) {
  customElements.define('ax-article-toc', AxArticleToc);
}
